import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  Divider,
  TextField,
  Rating,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  alpha,
  LinearProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  X,
  Calendar,
  Clock,
  User,
  Send,
  MessageSquare,
  CheckCircle,
  XCircle,
  Play,
  AlertTriangle,
  FileText,
  Star,
  RefreshCw,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { DelegatedTask, DelegatedTaskStatus, DelegatedTaskFeedback } from '@/types';
import { delegatedTaskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import PriorityBadge from '@/shared/components/PriorityBadge';

// Status configuration
const statusConfig: Record<DelegatedTaskStatus, { label: string; color: string; icon: React.ElementType }> = {
  [DelegatedTaskStatus.PENDING]: { label: 'Pending Acceptance', color: '#f59e0b', icon: Clock },
  [DelegatedTaskStatus.ACCEPTED]: { label: 'Accepted', color: '#3b82f6', icon: CheckCircle },
  [DelegatedTaskStatus.IN_PROGRESS]: { label: 'In Progress', color: '#8b5cf6', icon: RefreshCw },
  [DelegatedTaskStatus.COMPLETED]: { label: 'Completed', color: '#16a34a', icon: CheckCircle },
  [DelegatedTaskStatus.REJECTED]: { label: 'Rejected', color: '#ef4444', icon: XCircle },
  [DelegatedTaskStatus.CANCELLED]: { label: 'Cancelled', color: '#6b7280', icon: XCircle },
};

interface DelegatedTaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  task: DelegatedTask;
  isAssignee: boolean; // true if current user is the one the task was delegated TO
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </Box>
  );
}

/**
 * DelegatedTaskDetailModal
 * Detailed view for a delegated task with feedback and status management
 */
const DelegatedTaskDetailModal: React.FC<DelegatedTaskDetailModalProps> = ({
  open,
  onClose,
  task,
  isAssignee,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(task.progressPercentage || 0);

  const status = statusConfig[task.status];
  const StatusIcon = status.icon;

  const isOverdue = dayjs(task.dueDate).isBefore(dayjs()) && 
                    task.status !== DelegatedTaskStatus.COMPLETED && 
                    task.status !== DelegatedTaskStatus.CANCELLED;

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status, data }: { taskId: string; status: DelegatedTaskStatus; data?: Record<string, unknown> }) =>
      delegatedTaskService.updateDelegatedTaskStatus(taskId, status, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.delegatedTasks });
      onClose();
    },
  });

  // Add feedback mutation
  const addFeedbackMutation = useMutation({
    mutationFn: ({ taskId, feedback }: { taskId: string; feedback: Partial<DelegatedTaskFeedback> }) =>
      delegatedTaskService.addFeedback(taskId, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.delegatedTasks });
      setFeedbackText('');
      setFeedbackRating(null);
    },
  });

  // Update progress mutation
  const updateProgressMutation = useMutation({
    mutationFn: ({ taskId, progress }: { taskId: string; progress: number }) =>
      delegatedTaskService.updateProgress(taskId, progress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.delegatedTasks });
    },
  });

  const handleAccept = () => {
    updateStatusMutation.mutate({ 
      taskId: task.id, 
      status: DelegatedTaskStatus.ACCEPTED 
    });
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) return;
    updateStatusMutation.mutate({ 
      taskId: task.id, 
      status: DelegatedTaskStatus.REJECTED,
      data: { rejectionReason }
    });
  };

  const handleStartProgress = () => {
    updateStatusMutation.mutate({ 
      taskId: task.id, 
      status: DelegatedTaskStatus.IN_PROGRESS 
    });
  };

  const handleComplete = () => {
    updateStatusMutation.mutate({ 
      taskId: task.id, 
      status: DelegatedTaskStatus.COMPLETED,
      data: { completionNotes }
    });
  };

  const handleCancel = () => {
    updateStatusMutation.mutate({ 
      taskId: task.id, 
      status: DelegatedTaskStatus.CANCELLED 
    });
  };

  const handleAddFeedback = () => {
    if (!feedbackText.trim()) return;
    addFeedbackMutation.mutate({
      taskId: task.id,
      feedback: {
        content: feedbackText,
        rating: feedbackRating || undefined,
        createdBy: currentUser?.id || '',
      },
    });
  };

  const handleUpdateProgress = () => {
    updateProgressMutation.mutate({
      taskId: task.id,
      progress: progressPercentage,
    });
  };

  const renderDetails = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Status Banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(status.color, 0.1),
          border: '1px solid',
          borderColor: alpha(status.color, 0.2),
        }}
      >
        <StatusIcon size={24} style={{ color: status.color }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: status.color }}>
            {status.label}
          </Typography>
          {task.status === DelegatedTaskStatus.REJECTED && task.rejectionReason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {task.rejectionReason}
            </Typography>
          )}
        </Box>
        {isOverdue && (
          <Chip
            icon={<AlertTriangle size={14} />}
            label="Overdue"
            size="small"
            sx={{
              bgcolor: alpha('#ef4444', 0.1),
              color: '#ef4444',
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {/* Task Info Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Delegated By */}
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Delegated By
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
              {task.assignedByName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body2" fontWeight={500}>
              {task.assignedByName}
            </Typography>
          </Box>
        </Box>

        {/* Assigned To */}
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Assigned To
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
              {task.assignedToName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body2" fontWeight={500}>
              {task.assignedToName}
            </Typography>
          </Box>
        </Box>

        {/* Start Date */}
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Start Date
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Calendar size={16} style={{ color: '#6b7280' }} />
            <Typography variant="body2">
              {task.startDate ? dayjs(task.startDate).format('MMM D, YYYY') : 'Not set'}
            </Typography>
          </Box>
        </Box>

        {/* Due Date */}
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: isOverdue ? alpha('#ef4444', 0.05) : 'grey.50' }}>
          <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'} gutterBottom>
            Due Date
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Clock size={16} style={{ color: isOverdue ? '#ef4444' : '#6b7280' }} />
            <Typography variant="body2" fontWeight={isOverdue ? 600 : 400} color={isOverdue ? 'error.main' : 'text.primary'}>
              {dayjs(task.dueDate).format('MMM D, YYYY h:mm A')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Priority & Tags */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <PriorityBadge priority={task.priority} size="medium" />
        {task.isUrgent && (
          <Chip
            label="Urgent"
            size="small"
            sx={{
              bgcolor: alpha('#ef4444', 0.1),
              color: '#ef4444',
              fontWeight: 600,
            }}
          />
        )}
        {task.tags?.map((tag) => (
          <Chip key={tag} label={tag} size="small" variant="outlined" />
        ))}
      </Box>

      {/* Description */}
      {task.description && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {task.description}
          </Typography>
        </Box>
      )}

      {/* Notes from Delegator */}
      {task.notes && (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#3b82f6', 0.05), border: '1px solid', borderColor: alpha('#3b82f6', 0.2) }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#3b82f6' }}>
            Notes from Delegator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {task.notes}
          </Typography>
        </Box>
      )}

      {/* Progress (for in-progress tasks) */}
      {(task.status === DelegatedTaskStatus.IN_PROGRESS || task.status === DelegatedTaskStatus.ACCEPTED) && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Progress</Typography>
            <Typography variant="body2" color="text.secondary">
              {progressPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
          {isAssignee && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="number"
                size="small"
                value={progressPercentage}
                onChange={(e) => setProgressPercentage(Math.min(100, Math.max(0, Number(e.target.value))))}
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 100 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleUpdateProgress}
                disabled={updateProgressMutation.isPending}
              >
                Update
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Completion Notes */}
      {task.completionNotes && task.status === DelegatedTaskStatus.COMPLETED && (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#16a34a', 0.05), border: '1px solid', borderColor: alpha('#16a34a', 0.2) }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#16a34a' }}>
            Completion Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {task.completionNotes}
          </Typography>
        </Box>
      )}

      {/* Estimated vs Actual Hours */}
      {(task.estimatedHours || task.actualHours) && (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {task.estimatedHours && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Estimated
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {task.estimatedHours}h
              </Typography>
            </Box>
          )}
          {task.actualHours && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Actual
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {task.actualHours}h
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const renderFeedback = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Add Feedback Form */}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Feedback
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Share your feedback or comments..."
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rating (optional):
            </Typography>
            <Rating
              value={feedbackRating}
              onChange={(_, newValue) => setFeedbackRating(newValue)}
              size="small"
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleAddFeedback}
            disabled={!feedbackText.trim() || addFeedbackMutation.isPending}
            startIcon={<MessageSquare size={14} />}
          >
            Add Feedback
          </Button>
        </Box>
      </Box>

      {/* Feedback List */}
      {task.feedback && task.feedback.length > 0 ? (
        <List sx={{ p: 0 }}>
          {task.feedback.map((fb, index) => (
            <ListItem
              key={fb.id || index}
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: index < task.feedback!.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  U
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {fb.createdBy === task.assignedBy ? task.assignedByName : task.assignedToName}
                    </Typography>
                    {fb.rating && (
                      <Rating value={fb.rating} size="small" readOnly />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {fb.content}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {dayjs(fb.createdAt).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <MessageSquare size={40} style={{ color: '#d1d5db' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No feedback yet
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderActions = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Assignee Actions */}
      {isAssignee && (
        <>
          {/* Accept/Reject for pending tasks */}
          {task.status === DelegatedTaskStatus.PENDING && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info">
                This task has been delegated to you. Please accept or reject it.
              </Alert>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle size={16} />}
                  onClick={handleAccept}
                  disabled={updateStatusMutation.isPending}
                  fullWidth
                >
                  Accept Task
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<XCircle size={16} />}
                  onClick={() => setTabValue(3)} // Switch to reject tab
                  fullWidth
                >
                  Reject Task
                </Button>
              </Box>
            </Box>
          )}

          {/* Start Progress for accepted tasks */}
          {task.status === DelegatedTaskStatus.ACCEPTED && (
            <Button
              variant="contained"
              startIcon={<Play size={16} />}
              onClick={handleStartProgress}
              disabled={updateStatusMutation.isPending}
              fullWidth
            >
              Start Working
            </Button>
          )}

          {/* Complete for in-progress tasks */}
          {task.status === DelegatedTaskStatus.IN_PROGRESS && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Completion Notes"
                multiline
                rows={3}
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Add notes about the completed work..."
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle size={16} />}
                onClick={handleComplete}
                disabled={updateStatusMutation.isPending}
                fullWidth
              >
                Mark as Complete
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Delegator Actions */}
      {!isAssignee && (
        <>
          {task.status !== DelegatedTaskStatus.COMPLETED && 
           task.status !== DelegatedTaskStatus.CANCELLED && 
           task.status !== DelegatedTaskStatus.REJECTED && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<XCircle size={16} />}
              onClick={handleCancel}
              disabled={updateStatusMutation.isPending}
            >
              Cancel Task
            </Button>
          )}
        </>
      )}

      {/* Reject Form (for assignee) */}
      {isAssignee && task.status === DelegatedTaskStatus.PENDING && tabValue === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Reason for Rejection"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please explain why you cannot accept this task..."
            required
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setTabValue(0)}
              fullWidth
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={!rejectionReason.trim() || updateStatusMutation.isPending}
              fullWidth
            >
              Confirm Rejection
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              {task.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Created {dayjs(task.createdAt).format('MMM D, YYYY')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <Box sx={{ px: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab
            icon={<FileText size={16} />}
            iconPosition="start"
            label="Details"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<MessageSquare size={16} />}
            iconPosition="start"
            label={`Feedback (${task.feedback?.length || 0})`}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<Play size={16} />}
            iconPosition="start"
            label="Actions"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
        </Tabs>
      </Box>

      <DialogContent>
        <TabPanel value={tabValue} index={0}>
          {renderDetails()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderFeedback()}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderActions()}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderActions()}
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelegatedTaskDetailModal;
